import React from "react";
import TextReveal from "./TextReveal";

/**
 * Premium editorial page hero with:
 *  - Full-bleed image (Ken Burns)
 *  - Animated clip-reveal title
 *  - Optional eyebrow + subtitle + meta grid
 *  - Variants: "dark" (default), "light" (bone background + small image strip)
 */
export default function PageHero({
  eyebrow = "",
  title,
  italic = "",
  subtitle = "",
  image,
  meta = [],
  variant = "dark",
  height = "tall",
}) {
  const heightClass = height === "tall" ? "min-h-[82vh]" : "min-h-[55vh]";

  if (variant === "light") {
    return (
      <section className="relative bg-bone pt-40 pb-24 -mt-[78px] overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.08] pointer-events-none">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          {eyebrow && <div className="text-eyebrow text-amber mb-6"><TextReveal>— {eyebrow}</TextReveal></div>}
          <h1 className="font-display-tight text-6xl md:text-7xl lg:text-[10rem] leading-[0.88] text-ink text-balance">
            <TextReveal>{title}</TextReveal>
            {italic && <><br /><TextReveal delay={140}><span className="italic font-display text-amber">{italic}</span></TextReveal></>}
          </h1>
          {subtitle && <p className="mt-8 text-ink-soft text-lg md:text-xl max-w-2xl leading-relaxed">{subtitle}</p>}
        </div>
      </section>
    );
  }

  return (
    <section className={`relative ${heightClass} w-full overflow-hidden bg-ink text-bone -mt-[78px]`}>
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover kb" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/75" />
      <div className="absolute inset-6 lg:inset-10 border border-bone/15 pointer-events-none" />

      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 lg:px-10 pt-32 pb-14 flex flex-col justify-end">
        {eyebrow && <div className="text-eyebrow text-amber mb-6"><TextReveal>— {eyebrow}</TextReveal></div>}
        <h1 className="font-display-tight text-5xl md:text-7xl lg:text-[11rem] leading-[0.88] text-balance max-w-5xl">
          <TextReveal>{title}</TextReveal>
          {italic && <><br /><TextReveal delay={140}><span className="italic font-display text-amber">{italic}</span></TextReveal></>}
        </h1>
        {subtitle && <p className="mt-8 text-bone/80 text-lg md:text-xl max-w-2xl leading-relaxed">{subtitle}</p>}

        {meta.length > 0 && (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-bone/15 pt-8 max-w-4xl">
            {meta.map((m, i) => (
              <div key={i}>
                <div className="text-eyebrow text-bone/50 mb-2">{m.label}</div>
                <div className="font-display text-xl text-bone">{m.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
